import {Injectable} from "@angular/core";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {GlossaryPipelineService} from "./glossary.pipeline.service";
import {
    IWordTranslations,
    IGlossaryEntry,
    ITranslation,
    IVocabularyEntry, ISelection, IWord, ISentence, IText
} from "../../api/data-model";

@Injectable()
export class VocabularyPipelineService {
    constructor(private glossaryService:GlossaryPipelineService, private logService: BugsnagLoggingService) {
    }

    public useOldStyleUI(contentVersion: string): boolean {
        let contentVersionInteger = contentVersion.match(/^\d+$/);
        // released versions of the content as of the time the new word wall super user UI was developed (Jan 2019)
        // used the old way of selecting word wall, and if we have to edit word wall for those stories,
        // we need to present the original UI
        return !!(contentVersionInteger && contentVersionInteger.length && (Number(contentVersionInteger[0]) <= 2));
    }

    public getVocabularyEntries(vocabList:IVocabularyEntry[]): VocabularyEntry[] {
        let vocabularyEntries:VocabularyEntry[] = [];
        vocabList.forEach((vocabItem) => {
            let glossaryEntry:IGlossaryEntry = this.glossaryService.getEntryUsingV1Id(vocabItem.glossary).entry;
            let vocabEntry:VocabularyEntry = new VocabularyEntry(vocabItem);
            if (glossaryEntry) {
                vocabEntry.initFromSelection(glossaryEntry, vocabEntry.translations[VocabularyEntry.LANGUAGE]);
            }
            vocabEntry.isEditable = false;
            vocabularyEntries.push(vocabEntry);
        });
        return vocabularyEntries;
    }

    public getLinkedVocabulary(selection: ISelection): VocabularyEntry[] {
        let vocabularyEntries:VocabularyEntry[] = [];
        selection.text.forEach((text:IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    sentence.words.forEach((word: IWord) => {
                        if (word.glossary) {
                            this.addToVocabList(word, vocabularyEntries);
                        }
                    });
                });
            }
        });
        selection.vocabularyEntries.forEach((entryInExistingList) => {
            let index = vocabularyEntries.findIndex((entry) => {
                return entry.glossary === entryInExistingList.glossary;
            });
            if (index === -1) {
                this.logService.log(selection.metaData.referenceStory +
                            " Vocabulary (Word Wall) list contains glossary not linked in selection: "
                            + entryInExistingList.word);
            } else {
                vocabularyEntries[index]._isSelected = true;
            }
        });
        return vocabularyEntries;
    }

    private addToVocabList(word: IWord, vocabularyEntries: VocabularyEntry[]): void {
        // if the glossary entry associated with word is not already in the list, add it
        if (vocabularyEntries.filter((entry) => {
                return entry.glossary === word.glossary;
            }).length === 0) {
            let glossaryEntry = this.glossaryService.getEntryUsingV1Id(word.glossary).entry;
            if (glossaryEntry) {
                vocabularyEntries.push(new VocabularyEntry({
                    glossary: word.glossary,
                    word: glossaryEntry.word,
                    translations: word.translations
                }));
            } else {
                console.log("glossary entry not found for: " + word.glossary);
            }
        }
    }

    public getVocabularyEntryFromGlossary(term, glossaryEntries:IGlossaryEntry[]): VocabularyEntry {
        let vocabularyEntry = new VocabularyEntry({dictionary: [], translations: null, glossary: "", word: ""});
        vocabularyEntry.initFromGlossary(term, glossaryEntries);
        vocabularyEntry._isEditable = true;
        return vocabularyEntry;
    }

}
export interface IDefinitionDetail {
    id: string;
    definition: string;
    context: string;
    translationOptions: ITranslation[];
}

// VocabularyEntry takes the existing vocabulary data and adds additional properties to allow for editing and selecting
// among multiple glossary entries.
export class VocabularyEntry implements IVocabularyEntry {
    word: string;
    glossary: string;
    dictionary?: string[];
    translations: IWordTranslations;
    definitionOptions: IDefinitionDetail[];
    _isEditable: boolean;
    _isSelected: boolean;
    definitionIndex: number; // index of the currently selected definition if searching glossary
    translationIndex: number; // index of the currently selection translation (in the currently selected definition)
    // TODO: just abstracting this for now, at some point will need to generalize to other languages
    static LANGUAGE = "es";

    constructor(private vocabEntry: IVocabularyEntry) {
        this.clear();
        this.word = vocabEntry.word;
        this.glossary = vocabEntry.glossary;
        this.dictionary = vocabEntry.dictionary;
        if (vocabEntry.translations) {
            this.translations = vocabEntry.translations;
        }
        this.definitionOptions = [];
        this._isEditable = false;
        this.definitionIndex = 0;
        this.translationIndex = 0;
    }

    get isEditable(): boolean {
        return this._isEditable;
    }

    set isEditable(value: boolean) {
        this._isEditable = value;
    }

    public clear(): void {
        this.word = "";
        this.glossary = "";
        this.dictionary = [];
        this.translations = {};
        this.translations[VocabularyEntry.LANGUAGE] = "";
        this.definitionOptions = [];
    }

    public initFromSelection(glossaryEntry: IGlossaryEntry, translationId: string): void {
        this.word = glossaryEntry.word;
        this.glossary = glossaryEntry.id;
        this.translations[VocabularyEntry.LANGUAGE] = translationId;
        console.log("trans: " + JSON.stringify(glossaryEntry.translations));
        let translationOptions = glossaryEntry.translations.filter((t) => (t.id === translationId)
                                                                          && (t.language === VocabularyEntry.LANGUAGE));
        this.definitionOptions = [{
            id: glossaryEntry.id,
            definition: glossaryEntry.definition.text,
            context: glossaryEntry.context.text,
            translationOptions: translationOptions
        }];
    }

    public initFromGlossary(term: string, glossaryEntries: IGlossaryEntry[]): void {
        this.clear();
        this.word = term;
        if (glossaryEntries && glossaryEntries.length) {
            glossaryEntries.forEach((glossaryEntry) => {
                console.log("trans: " + JSON.stringify(glossaryEntry.translations));
                let translationOptions = glossaryEntry.translations
                    .filter((t) => (t.language === VocabularyEntry.LANGUAGE));
                this.definitionOptions.push({
                    id: glossaryEntry.id,
                    definition: glossaryEntry.definition.text,
                    context: glossaryEntry.context.text,
                    translationOptions: translationOptions
                });
            });
            this.setDataFromIndices(0, 0);

        } else {
            this.definitionOptions = [{id: "", definition: "NO ENTRY FOUND", context: "",
                                    translationOptions: [{id: "", word: "", audio:"", language: ""}]}];
        }
    }

    public setDataFromIndices(definitionIndex: number, translationIndex:number): void {
        this.definitionIndex = definitionIndex;
        this.translationIndex = translationIndex;
        this.glossary = this.definitionOptions[definitionIndex].id;
        let translations = this.definitionOptions[definitionIndex].translationOptions.filter(
            (t) => (t.language === VocabularyEntry.LANGUAGE));
        this.translations[VocabularyEntry.LANGUAGE] = translations[translationIndex].id;
    }
}
